// RecordedDeedNotice — "Foreclosure deed recorded <date>" line on Property
// Detail (#2545). Evidence from the county Register of Deeds, matched to this
// property on a parcel key (property_rod_filings, written by cmd/rod-save).
//
// EVIDENCE ONLY: it never changes the status badge. A recorded trustee's deed
// shows the sale closed, but moving `status` from it is the status engine's
// call (founder decision, 2026-09-29). The copy says what was recorded, not
// what the property's status is.
//
// Renders NOTHING while loading, on error, when the viewer is locked, or when
// there is no deed — a missing deed is the normal case (only Gaston County is
// harvested so far), so none of those deserve UI.

import React, { useEffect, useState } from "react";
import { Property } from "../types";
import { apiClient } from "../lib/apiClient";
import { formatDate } from "../lib/format";

interface RODFiling {
  instrumentType: string;
  signal: string;
  recordedDate: string | null;
  book?: string;
  page?: string;
  docNumber: string;
}

interface RODFilingsResponse {
  locked: boolean;
  filings?: RODFiling[];
}

/** The newest foreclosure-deed filing, or null. The API already sorts newest first. */
export function latestForeclosureDeed(filings: RODFiling[] | undefined): RODFiling | null {
  return (filings || []).find((f) => f.signal === "foreclosure_deed") || null;
}

/** "Book 5663, Page 2012" — empty string when the ROD gave neither. */
export function bookPageLabel(f: Pick<RODFiling, "book" | "page">): string {
  if (f.book && f.page) return `Book ${f.book}, Page ${f.page}`;
  return "";
}

const RecordedDeedNotice: React.FC<{ property: Property }> = ({ property }) => {
  const [deed, setDeed] = useState<RODFiling | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDeed(null);
    apiClient
      .get<RODFilingsResponse>(`/properties/${property.id}/rod-filings`)
      .then(({ data }) => {
        if (!cancelled && !data.locked) setDeed(latestForeclosureDeed(data.filings));
      })
      .catch(() => {
        // Deliberately silent: this is supplementary evidence, not core data.
      });
    return () => {
      cancelled = true;
    };
  }, [property.id]);

  if (!deed) return null;

  const when = formatDate(deed.recordedDate, "");
  const bookPage = bookPageLabel(deed);
  return (
    <p
      className="recorded-deed-notice"
      title="From the county Register of Deeds. This is a recorded filing, not a change to the status above."
    >
      <strong>Foreclosure deed recorded{when ? ` ${when}` : ""}</strong>
      {bookPage ? ` — ${bookPage}` : ""}
    </p>
  );
};

export default RecordedDeedNotice;
